import React from 'react';
import { Globe } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AddCountryForm } from './countries/AddCountryForm';
import { CountryTable } from './countries/CountryTable';
import { CountryEditDialog } from './countries/CountryEditDialog';
import { CountriesFilters } from './countries/CountriesFilters';
import { useCountries } from './countries/hooks';
import { CountryItem } from './countries/types';
import { useCountry } from '@/contexts/country';

interface CountryManagementProps {
  isMobile?: boolean;
}

const CountryManagement: React.FC<CountryManagementProps> = ({ isMobile }) => {
  const { refreshCountries } = useCountry();
  
  const {
    countries,
    isLoading,
    editing,
    editData,
    dialogOpen,
    selectedCountry,
    setEditData,
    handleToggleActive,
    handleEdit,
    handleSaveEdit,
    handleDelete,
    setCountries,
    handleOpenDialog,
    handleCloseDialog,
    handleSaveCountryDialog,
    // Pagination props
    currentPage,
    totalPages,
    itemsPerPage,
    totalItems,
    setCurrentPage,
    setItemsPerPage,
    // Filter and search props
    searchQuery,
    setSearchQuery,
    selectedRegion,
    setSelectedRegion,
    selectedStatus,
    setSelectedStatus
  } = useCountries();

  const handleCountryAdded = (newCountry: CountryItem) => {
    setCountries([...countries, newCountry]);
    refreshCountries(); // Refresh countries in context
  };
  
  // Modified to refresh countries in context when status is toggled
  const handleCountryToggle = async (id: string) => {
    await handleToggleActive(id);
    refreshCountries(); // Refresh countries in context
  };
  
  // Modified to refresh countries in context when a country is updated
  const handleCountrySaved = async (country: CountryItem) => {
    await handleSaveCountryDialog(country);
    refreshCountries(); // Refresh countries in context
  };
  
  // Modified to refresh countries in context when a country is deleted
  const handleCountryDeleted = async (id: string) => {
    await handleDelete(id);
    refreshCountries(); // Refresh countries in context
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            Loading countries...
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Globe size={20} />
          Country Management
        </CardTitle>
        <CardDescription>
          Add, edit or remove countries from the platform
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          <AddCountryForm onCountryAdded={handleCountryAdded} isMobile={isMobile} />
          
          <CountriesFilters
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            selectedRegion={selectedRegion}
            onRegionChange={setSelectedRegion}
            selectedStatus={selectedStatus}
            onStatusChange={setSelectedStatus}
            isMobile={isMobile}
          />
          
          <CountryTable
            countries={countries}
            editing={editing}
            editData={editData}
            onEdit={handleEdit}
            onSave={handleSaveEdit}
            onDelete={handleCountryDeleted}
            onToggleActive={handleCountryToggle}
            onEditDataChange={setEditData}
            onOpenDialog={handleOpenDialog}
            isMobile={isMobile}
            currentPage={currentPage}
            totalPages={totalPages}
            itemsPerPage={itemsPerPage}
            totalItems={totalItems}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setItemsPerPage}
          />
          
          <CountryEditDialog 
            country={selectedCountry}
            open={dialogOpen}
            onClose={handleCloseDialog}
            onSave={handleCountrySaved}
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default CountryManagement;
